import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import {
  ArrowLeft as ArrowLeftIcon,
  CalendarBlank as CalendarIcon,
  CalendarCheck as CalendarCheckIcon,
  CurrencyDollar as CurrencyDollarIcon,
  File as FileIcon,
  ArrowCounterClockwise as RotateCcwIcon,
  Warning as WarningIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate, useParams } from 'react-router-dom';

import { getReservation, cancelReservation, payFee } from '@/api/reservations';
import { getPaymentMethods, type StoredPaymentMethod } from '@/api/payment-methods';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';
import { Seo } from '@/components/seo';
import { paths } from '@/paths';
import { getS3PresignedUrlFromPath } from '@/utils/s3';
import { toast } from '@/components/core/v2/toaster';
import type { AmenityReservation } from '@/types/v2/reservation';
import { useUser } from '@/hooks/v2/use-user';

dayjs.extend(utc);

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
  waitlisted: 'info',
};

// ── Shared Paper section style ────────────────────────────────────────────────

const sectionHeaderSx = {
  px: 3,
  py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
} as const;

function formatPaymentMethod(method: StoredPaymentMethod | null | undefined): string {
  if (!method) return 'your default saved payment method';

  if (method.type === 'us_bank_account') {
    return `${method.bank_name ?? 'Bank account'} ending in ${method.last4}`;
  }

  return `${method.brand ?? 'Card'} ending in ${method.last4}`;
}

// ── Documents section ─────────────────────────────────────────────────────────

function DocumentsSection({
  documents,
}: {
  documents: NonNullable<AmenityReservation['documents']>;
}): React.JSX.Element {
  const [presignedUrls, setPresignedUrls] = React.useState<Record<string, string>>({});
  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewName, setPreviewName] = React.useState<string | null>(null);

  const isImage = (name?: string) => /\.(jpe?g|png|gif|webp)$/i.test(name ?? '');
  const isPdf = (name?: string) => /\.pdf$/i.test(name ?? '');

  const handlePreview = async (doc: NonNullable<AmenityReservation['documents']>[0]) => {
    const cached = presignedUrls[doc.id];
    if (cached) {
      setPreviewUrl(cached);
      setPreviewName(doc.file_name ?? doc.document_type);
      return;
    }
    setLoadingId(doc.id);
    try {
      const url = await getS3PresignedUrlFromPath(doc.file_key);
      setPresignedUrls((prev) => ({ ...prev, [doc.id]: url }));
      setPreviewUrl(url);
      setPreviewName(doc.file_name ?? doc.document_type);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <Box sx={sectionHeaderSx}>
        <FileIcon size={18} weight="duotone" />
        <Typography variant="subtitle2" fontWeight={600}>
          Submitted Documents
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          {documents.map((doc) => (
            <Box
              key={doc.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {doc.document_type}
                </Typography>
                {doc.file_name && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {doc.file_name}
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePreview(doc)}
                disabled={loadingId === doc.id}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {loadingId === doc.id ? 'Loading…' : 'Preview'}
              </Button>
            </Box>
          ))}
        </Stack>
      </Box>

      {previewUrl && (
        <>
          <Divider />
          <Box sx={{ ...sectionHeaderSx, justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {previewName ?? 'Document Preview'}
            </Typography>
            <Button size="small" variant="text" onClick={() => setPreviewUrl(null)}>
              Close
            </Button>
          </Box>
          <Box sx={{ p: 0 }}>
            {isImage(previewName ?? '') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={previewName ?? 'document'}
                sx={{ width: '100%', maxHeight: 600, objectFit: 'contain', display: 'block' }}
              />
            ) : isPdf(previewName ?? '') ? (
              <Box
                component="iframe"
                src={previewUrl}
                sx={{ width: '100%', height: 600, border: 'none', display: 'block' }}
                title="Document Preview"
              />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Preview not available for this file type.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open File
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MyReservationDetailPage(): React.JSX.Element {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { community } = useUser();
  const confirm = useConfirm();

  const { data: reservation, isLoading, error } = useQuery({
    queryKey: ['my-reservation', reservationId],
    queryFn: () => getReservation(reservationId ?? '', { include: 'amenity,unit,documents' }),
    enabled: !!reservationId,
  });

  const { data: paymentMethodsData } = useQuery({
    queryKey: ['payment-methods', community?.id],
    queryFn: () => getPaymentMethods(community?.id ?? ''),
    enabled: !!community?.id,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelReservation(id, {}),
    onSuccess: () => {
      toast.success('Reservation cancelled');
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservation', reservationId] });
      navigate(paths.reservations.index);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Failed to cancel reservation');
    },
  });

  const payFeeMutation = useMutation({
    mutationFn: (id: string) => payFee(id),
    onSuccess: () => {
      toast.success('Fee paid successfully');
      queryClient.invalidateQueries({ queryKey: ['my-reservation', reservationId] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Failed to process payment');
    },
  });

  const [payFeeDialogOpen, setPayFeeDialogOpen] = React.useState(false);

  const handleCancelReservation = React.useCallback(async () => {
    if (!reservation) return;

    try {
      await confirm({
        title: 'Cancel Reservation',
        description: 'Are you sure you want to cancel this reservation? This cannot be undone.',
        confirmationText: 'Cancel Reservation',
        cancellationText: 'Keep Reservation',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
        cancellationButtonProps: { variant: 'outlined' },
      });

      cancelMutation.mutate(reservation.uuid ?? reservation.id);
    } catch {
      // User dismissed the confirmation dialog.
    }
  }, [cancelMutation, confirm, reservation]);

  // ── Error / not-found state ──
  if (!isLoading && (error || !reservation)) {
    return (
      <>
        <Seo title="Reservation Not Found" />
        <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CalendarCheckIcon
                color={theme.palette.text.disabled}
                size={64}
                weight="duotone"
                style={{ marginBottom: 24 }}
              />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                Reservation not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This reservation doesn&apos;t exist or you don&apos;t have access to it.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => window.history.back()} variant="outlined">
                  Go Back
                </Button>
                <Button href={paths.reservations.index} variant="contained">
                  All Reservations
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  const status = (reservation?.status ?? '') as string;
  const hasRefund = !!reservation?.deposit_refunded_at;
  const hasDamage = !!reservation?.damage_charge_amount;
  const hasDocs = (reservation?.documents?.length ?? 0) > 0;

  const totalCharged = Number(reservation?.total_fee_amount_charged ?? 0);
  const depositCharged = Number(reservation?.deposit_amount_charged ?? 0);
  const feeCharged = Number(reservation?.fee_amount_charged ?? 0);
  const remainingFee = feeCharged > 0
    ? (feeCharged <= totalCharged && depositCharged > 0 ? Math.max(0, totalCharged - depositCharged) : feeCharged)
    : 0;
  const canShowRemainingFee = ['pending', 'approved'].includes(status) && !hasRefund;
  const canCancel =
    !!reservation &&
    ['pending', 'approved'].includes(status) &&
    dayjs.utc(reservation.start_time).isAfter(dayjs.utc());

  const amenityName = reservation?.amenity?.name ?? 'Reservation';
  const refId = (reservation?.uuid ?? reservation?.id ?? '').toString().substring(0, 8).toUpperCase();
  const defaultPaymentMethod =
    paymentMethodsData?.payment_methods.find((method) => method.is_default) ?? paymentMethodsData?.payment_methods[0] ?? null;
  const paymentMethodLabel = formatPaymentMethod(defaultPaymentMethod);

  return (
    <>
      <Seo title={amenityName} />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            {isLoading ? (
              <Stack spacing={1}>
                <Skeleton width={160} height={20} />
                <Skeleton width={280} height={36} />
                <Skeleton width={120} height={20} />
              </Stack>
            ) : (
              <>
                <Button
                  startIcon={<ArrowLeftIcon size={16} />}
                  variant="text"
                  size="small"
                  onClick={() => navigate(paths.reservations.index)}
                  sx={{ mb: 1, textTransform: 'none', color: 'text.secondary', px: 0 }}
                >
                  All Reservations
                </Button>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {amenityName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      #{refId}
                      {reservation?.created_at && (
                        <> &middot; Requested {dayjs(reservation.created_at).format('MMM D, YYYY')}</>
                      )}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {canCancel && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleCancelReservation}
                        disabled={cancelMutation.isPending}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {cancelMutation.isPending ? 'Cancelling…' : 'Cancel Reservation'}
                      </Button>
                    )}
                    <Chip
                      label={status.replace(/_/g, ' ').toUpperCase()}
                      color={statusColors[status] ?? 'default'}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>
                </Stack>
              </>
            )}
          </Container>
        </Box>

        {/* ── Body ── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          {isLoading ? (
            <Grid container spacing={4}>
              <Grid md={8} xs={12}>
                <Skeleton variant="rounded" height={340} sx={{ borderRadius: 2 }} />
              </Grid>
              <Grid md={4} xs={12}>
                <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={4}>
              {/* ── Left column: details + notes ── */}
              <Grid md={8} xs={12}>
                <Paper
                  elevation={0}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
                >
                  {/* Reservation Details */}
                  <Box sx={sectionHeaderSx}>
                    <CalendarIcon size={18} weight="duotone" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Reservation Details
                    </Typography>
                  </Box>
                  <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 24px' }}>
                    <PropertyItem name="Amenity" value={reservation?.amenity?.name ?? '—'} />
                    <PropertyItem
                      name="Date"
                      value={dayjs.utc(reservation!.start_time).local().format('MMM D, YYYY')}
                    />
                    <PropertyItem
                      name="Time"
                      value={`${dayjs.utc(reservation!.start_time).local().format('h:mm A')} – ${dayjs.utc(reservation!.end_time).local().format('h:mm A')}`}
                    />
                    {(reservation?.guest_count ?? 0) > 0 && (
                      <PropertyItem name="Guests" value={reservation!.guest_count} />
                    )}
                    {reservation?.purpose && (
                      <PropertyItem name="Purpose" value={reservation.purpose} />
                    )}
                    {reservation?.special_requests && (
                      <PropertyItem name="Special Requests" value={reservation.special_requests} />
                    )}
                    {reservation?.alcohol_served && (
                      <PropertyItem name="Alcohol Served" value="Yes" />
                    )}
                    {reservation?.unit && (
                      <PropertyItem
                        name="Unit"
                        value={reservation.unit.unit_number ?? reservation.unit.name}
                      />
                    )}
                  </PropertyList>

                  {/* Rejection / Cancellation / Approval notes */}
                  {(reservation?.rejected_reason || reservation?.cancellation_reason || reservation?.approval_notes) && (
                    <>
                      <Divider />
                      <Box sx={{ p: 3 }}>
                        {reservation?.rejected_reason && (
                          <Alert severity="error" sx={{ mb: reservation.approval_notes ? 2 : 0 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Rejection Reason
                            </Typography>
                            <Typography variant="body2">{reservation.rejected_reason}</Typography>
                          </Alert>
                        )}
                        {reservation?.cancellation_reason && (
                          <Alert severity="error" sx={{ mb: reservation.approval_notes ? 2 : 0 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Cancellation Reason
                            </Typography>
                            <Typography variant="body2">{reservation.cancellation_reason}</Typography>
                          </Alert>
                        )}
                        {reservation?.approval_notes && (
                          <Alert severity="success">
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Manager Note
                            </Typography>
                            <Typography variant="body2">{reservation.approval_notes}</Typography>
                          </Alert>
                        )}
                      </Box>
                    </>
                  )}

                  {/* Documents */}
                  {hasDocs && (
                    <>
                      <Divider />
                      <DocumentsSection documents={reservation!.documents!} />
                    </>
                  )}
                </Paper>
              </Grid>

              {/* ── Right column: payment + sidebar ── */}
              <Grid md={4} xs={12}>
                <Stack spacing={3}>
                  {/* Payment */}
                  {(reservation?.deposit_amount_charged || reservation?.total_fee_amount_charged || reservation?.fee_amount_charged) && (
                    <Paper
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: { md: 'sticky' },
                        top: { md: 24 },
                      }}
                    >
                      <Box sx={sectionHeaderSx}>
                        <CurrencyDollarIcon size={18} weight="duotone" />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Payment
                        </Typography>
                      </Box>
                      <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 24px' }}>
                        {/* Total Fee */}
                        {reservation?.total_fee_amount_charged && Number(reservation.total_fee_amount_charged) > 0 && (
                          <PropertyItem
                            name="Total Fee"
                            value={
                              <Typography variant="body2" fontWeight={600}>
                                ${Number(reservation.total_fee_amount_charged).toFixed(2)}
                              </Typography>
                            }
                          />
                        )}

                        {/* Deposit */}
                        {reservation?.deposit_amount_charged && Number(reservation.deposit_amount_charged) > 0 && (
                          <>
                            <PropertyItem
                              name="Deposit"
                              value={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={hasRefund ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                                  >
                                    ${Number(reservation.deposit_amount_charged).toFixed(2)}
                                  </Typography>
                                  {hasRefund ? (
                                    <Chip label="Refunded" size="small" color="info" />
                                  ) : reservation.deposit_collected ? (
                                    <Chip label="Paid" size="small" color="success" />
                                  ) : (
                                    <Chip label="Not paid" size="small" variant="outlined" />
                                  )}
                                </Stack>
                              }
                            />
                            {reservation?.deposit_paid_at && !hasRefund && reservation.deposit_collected && (
                              <PropertyItem
                                name="Deposit Paid"
                                value={dayjs(reservation.deposit_paid_at).format('MMM D, YYYY')}
                              />
                            )}
                          </>
                        )}

                        {/* Remaining Fee */}
                        {remainingFee > 0 && canShowRemainingFee && (
                          <>
                            <PropertyItem
                              name="Remaining Fee"
                              value={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2">
                                    ${remainingFee.toFixed(2)}
                                  </Typography>
                                  {reservation?.fee_collected ? (
                                    <Chip label="Paid" size="small" color="success" />
                                  ) : (
                                    <Chip label="Due" size="small" color="warning" variant="outlined" />
                                  )}
                                </Stack>
                              }
                            />
                            {reservation?.fee_paid_at && reservation.fee_collected && (
                              <PropertyItem
                                name="Fee Paid"
                                value={dayjs(reservation.fee_paid_at).format('MMM D, YYYY')}
                              />
                            )}
                          </>
                        )}
                      </PropertyList>

                      {/* Pay Fee button */}
                      {reservation?.fee_payment_due && remainingFee > 0 && (
                        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => setPayFeeDialogOpen(true)}
                            disabled={payFeeMutation.isPending}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            {payFeeMutation.isPending
                              ? 'Processing...'
                              : `Pay $${remainingFee.toFixed(2)} Remaining Fee`}
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            Must be paid before your reservation date
                          </Typography>
                        </Box>
                      )}

                      {/* Pending approval message */}
                      {remainingFee > 0 && status === 'pending' && !hasRefund && (
                        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Alert severity="info" sx={{ mb: 0 }}>
                            Once your reservation is approved, you'll be able to pay the remaining fee of <strong>${remainingFee.toFixed(2)}</strong> here.
                          </Alert>
                        </Box>
                      )}
                    </Paper>
                  )}

                  {/* Deposit Refund */}
                  {hasRefund && (
                    <Paper
                      elevation={0}
                      sx={{ border: '1px solid', borderColor: 'info.light', borderRadius: 2, overflow: 'hidden' }}
                    >
                      <Box sx={{ ...sectionHeaderSx, bgcolor: 'info.lighter' }}>
                        <RotateCcwIcon size={18} weight="duotone" color={theme.palette.info.main} />
                        <Typography variant="subtitle2" fontWeight={600} color="info.main">
                          Deposit Refund
                        </Typography>
                      </Box>
                      <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 24px' }}>
                        <PropertyItem
                          name="Amount"
                          value={
                            <Typography variant="body2" fontWeight={600} color="info.main">
                              ${Number(reservation?.deposit_refund_amount ?? reservation?.deposit_amount_charged ?? 0).toFixed(2)}
                            </Typography>
                          }
                        />
                        <PropertyItem
                          name="Refunded On"
                          value={dayjs(reservation?.deposit_refunded_at).format('MMM D, YYYY h:mm A')}
                        />
                        {reservation?.deposit_refund_reason && (
                          <PropertyItem name="Reason" value={reservation.deposit_refund_reason} />
                        )}
                      </PropertyList>
                    </Paper>
                  )}

                  {/* Damage Charge */}
                  {hasDamage && (
                    <Paper
                      elevation={0}
                      sx={{ border: '1px solid', borderColor: 'error.light', borderRadius: 2, overflow: 'hidden' }}
                    >
                      <Box sx={{ ...sectionHeaderSx, bgcolor: 'error.lighter' }}>
                        <WarningIcon size={18} weight="duotone" color={theme.palette.error.main} />
                        <Typography variant="subtitle2" fontWeight={600} color="error.main">
                          Damage Charge
                        </Typography>
                      </Box>
                      <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 24px' }}>
                        <PropertyItem
                          name="Amount"
                          value={
                            <Typography variant="body2" fontWeight={600} color="error.main">
                              ${Number(reservation?.damage_charge_amount).toFixed(2)}
                            </Typography>
                          }
                        />
                        {reservation?.damage_charged_at && (
                          <PropertyItem
                            name="Charged On"
                            value={dayjs(reservation.damage_charged_at).format('MMM D, YYYY h:mm A')}
                          />
                        )}
                        {reservation?.damage_charge_notes && (
                          <PropertyItem name="Notes" value={reservation.damage_charge_notes} />
                        )}
                      </PropertyList>
                    </Paper>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>

      {/* ── Pay Fee Confirmation Dialog ── */}
      <Dialog
        open={payFeeDialogOpen}
        onClose={() => !payFeeMutation.isPending && setPayFeeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          Confirm Payment
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              You are about to pay the remaining reservation fee. This charge will be processed immediately using {paymentMethodLabel}.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Amenity</Typography>
                  <Typography variant="body2" fontWeight={600}>{amenityName}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Reservation Date</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {reservation?.start_time ? dayjs.utc(reservation.start_time).format('MMM D, YYYY') : '—'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Total Fee</Typography>
                  <Typography variant="body2">${totalCharged.toFixed(2)}</Typography>
                </Stack>
                {depositCharged > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Deposit Paid</Typography>
                    <Typography variant="body2" color="success.main">−${depositCharged.toFixed(2)}</Typography>
                  </Stack>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700}>Amount Due Now</Typography>
                  <Typography variant="subtitle2" fontWeight={700}>${remainingFee.toFixed(2)}</Typography>
                </Stack>
              </Stack>
            </Paper>

            <Typography variant="caption" color="text.secondary">
              By confirming, you authorize an immediate charge of ${remainingFee.toFixed(2)} to {paymentMethodLabel}. This payment is non-refundable.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setPayFeeDialogOpen(false)}
            disabled={payFeeMutation.isPending}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!reservationId) return;
              payFeeMutation.mutate(reservationId, {
                onSuccess: () => setPayFeeDialogOpen(false),
              });
            }}
            disabled={payFeeMutation.isPending}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {payFeeMutation.isPending ? 'Processing...' : `Pay $${remainingFee.toFixed(2)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
